import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

import LungReferralImage from "assets/images/lungReferralImage.png";
import { FormField } from "components/form/FormField/FormField";

import { scrollToFirstError } from "../../utils/scrollToFirstError";
import { ItemFormWrapper } from "../ItemFormWrapper/ItemFormWrapper";
import { CurrentMedicationSection } from "./CurrentMedicationSection";
import style from "./LungsAdditionalInfoForm.module.css";
import { RadInvestigation } from "./RadInvestigation";

const radioSx = {
  "&.Mui-checked": {
    color: "var(--green-900)",
  },
};

export const LungsAdditionalInfoForm = ({ onBack, onContinue, form, data }) => {
  const handleFormSubmit = (values) => {

    onContinue(values);
  };

  const [showSecondRadInvestigation, setShowSecondRadInvestigation] =
    useState(false);

  return (
    <>
      <img
        src={LungReferralImage}
        alt="National lung cancer rapid access service referral form"
      />
      <ItemFormWrapper
        onBack={onBack}
        onContinue={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
        bg="#F0FFF0"
        padding="0px"
      >
        <div
          className={style.inputGroupGrid5Columns}
          style={{ marginTop: "24px" }}
        >
          <div>
            <p className={style.referralFormLabel}>Patient Name</p>
            <p className={style.referralFormValue}>
              {data.firstName} {data.familyName}
            </p>
          </div>
          <div>
            <p className={style.referralFormLabel}>Gender</p>
            <p className={style.referralFormValue}>
              {data.gender
                ? data.gender.charAt(0).toUpperCase() +
                  data.gender.slice(1).toLowerCase()
                : ""}
            </p>
          </div>
          <div>
            <p className={style.referralFormLabel}>Date of Birth</p>
            <p className={style.referralFormValue}>{data.dateOfBirth}</p>
          </div>
          <div>
            <p className={style.referralFormLabel}>Receiving Site</p>
            <p className={style.referralFormValue}>{data.hospitalName}</p>
          </div>
          <div>
            <p className={style.referralFormLabel}>Referral Type</p>
            <p className={style.referralFormValue}>
              {" "}
              {data.referralType
                ? data.referralType.charAt(0).toUpperCase() +
                  data.referralType.slice(1).toLowerCase()
                : ""}
            </p>
          </div>
        </div>
        <div
          className={style.itemWrapper}
          style={{
            marginBottom: "0px",
            paddingBottom: "0px",
            borderBottom: "none",
          }}
        >
          <FormProvider {...form}>
            <form
              className={style.addReferralForm}
              onSubmit={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
              style={{ gap: "0" }}
            >
              <div className={style.item}>
                <div className={style.formItemWrapper}>
                  <div></div>
                  <div></div>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <div className={style.info}>
                      Main indications for referral are an{" "}
                      <b>abnormal chest x-ray</b> or <b>haemoptysis</b>
                    </div>
                    <a
                      href="https://test.healthlink.ie/Support/Lung%20Cancer%20Guidelines.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={style.link}
                    >
                      Lung Cancer Guidelines
                    </a>
                  </div>
                </div>
                <FormField name="symptoms">
                  <div className={style.formItemWrapper}>
                    <label className={style.addReferralFormLabel}>
                      Symptom / Reason for Referral
                    </label>
                    <div style={{ color: "#F72924" }}>*</div>
                    <label className={style.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={form.watch("haemoptysis")}
                        onChange={(e) => {
                          form.setValue("haemoptysis", e.target.checked);
                        }}
                      />
                      Haemoptysis
                    </label>
                  </div>
                </FormField>
                <FormField name="otherPersistentUnexplainedSymptoms">
                  <div
                    className={style.formItemWrapper}
                    style={{ marginTop: "-40px" }}
                  >
                    <div></div>
                    <div></div>
                    <div>
                      <label
                        className={style.addReferralFormLabel}
                        style={{ fontWeight: "400", fontSize: "13px" }}
                      >
                        Other Persistent Unexplained Symptoms:
                      </label>
                      <textarea
                        className={style.winXpInputWrapper}
                        style={{ width: "100%" }}
                        {...form.register("otherPersistentUnexplainedSymptoms")}
                      />
                    </div>
                  </div>
                </FormField>

                <FormField name="clinicalExamination">
                  <div className={style.formItemWrapper}>
                    <label className={style.addReferralFormLabel}>
                      Clinical Examination
                    </label>
                    <div style={{ color: "#F72924" }}>*</div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label
                        className={style.checkboxLabel}
                        style={{ maxWidth: "180px" }}
                      >
                        <input
                          type="checkbox"
                          checked={form.watch("chestSigns")}
                          onChange={(e) => {
                            form.setValue("chestSigns", e.target.checked);
                          }}
                        />
                        Chest signs
                      </label>
                      <label
                        className={style.checkboxLabel}
                        style={{ maxWidth: "180px" }}
                      >
                        <input
                          type="checkbox"
                          checked={form.watch("lymphadenopathy")}
                          onChange={(e) => {
                            form.setValue("lymphadenopathy", e.target.checked);
                          }}
                        />
                        Lymphadenopathy
                      </label>
                      <label
                        className={style.checkboxLabel}
                        style={{ maxWidth: "180px" }}
                      >
                        <input
                          type="checkbox"
                          checked={form.watch("hepatosplenomegaly")}
                          onChange={(e) => {
                            form.setValue(
                              "hepatosplenomegaly",
                              e.target.checked
                            );
                          }}
                        />
                        Hepatomegaly
                      </label>
                      <label
                        className={style.checkboxLabel}
                        style={{ maxWidth: "180px" }}
                      >
                        <input
                          type="checkbox"
                          checked={form.watch("clubbing")}
                          onChange={(e) => {
                            form.setValue("clubbing", e.target.checked);
                          }}
                        />
                        Clubbing
                      </label>
                    </div>
                  </div>
                </FormField>
                {form.watch("chestSigns") === true && (
                  <FormField name="clinicalExamination">
                    <div
                      className={style.formItemWrapper}
                      style={{ marginTop: "-4px" }}
                    >
                      <div></div>
                      <div></div>
                      <div style={{ display: "flex", gap: "12px" }}>
                        <label
                          className={style.addReferralFormLabel}
                          style={{
                            fontWeight: "400",
                            width: "150px",
                            fontSize: "13px",
                          }}
                        >
                          Specify Chest Signs:
                        </label>
                        <input
                          className={style.winXpInputWrapper}
                          style={{ width: "338px" }}
                          {...form.register("specifiedChestSigns")}
                        />
                      </div>
                    </div>
                  </FormField>
                )}
                <FormField name="otherClinicalFindings">
                  <div
                    className={style.formItemWrapper}
                    style={{ marginTop: "-4px" }}
                  >
                    <div></div>
                    <div></div>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <label
                        className={style.addReferralFormLabel}
                        style={{
                          fontWeight: "400",
                          width: "150px",
                          fontSize: "13px",
                        }}
                      >
                        Other Clinical Findings:
                      </label>
                      <input
                        className={style.winXpInputWrapper}
                        style={{ width: "338px" }}
                        {...form.register("otherClinicalFindings")}
                      />
                    </div>
                  </div>
                </FormField>

                <FormField name="lungLabInvestigation">
                  <div className={style.formItemWrapper}>
                    <label className={style.addReferralFormLabel}>
                      Lab Investigation:
                    </label>
                    <div></div>
                    <input
                      className={style.winXpInputWrapper}
                      style={{ maxWidth: "250px" }}
                      {...form.register("lungLabInvestigation")}
                    />
                  </div>
                </FormField>

                <div className={style.formItemWrapper}>
                  <label className={style.addReferralFormLabel}>
                    Rad Investigation:
                  </label>
                  <div style={{ color: "#F72924" }}>*</div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    <div>
                      <RadInvestigation
                        name="radInvestigation1"
                        form={form}
                        button={
                          !showSecondRadInvestigation && (
                            <button
                              type="button"
                              className={style.radButton}
                              onClick={() => {
                                setShowSecondRadInvestigation(true);
                              }}
                            >
                              +
                            </button>
                          )
                        }
                      />

                      {showSecondRadInvestigation && (
                        <RadInvestigation
                          name="radInvestigation2"
                          form={form}
                          button={
                            <button
                              type="button"
                              className={style.radButton}
                              onClick={() => {
                                setShowSecondRadInvestigation(false);
                                form.setValue("radInvestigation2", "not-done");
                                form.setValue("radInvestigation2Hospital", "");
                                form.setValue("radInvestigation2Date", null);
                                form.setValue("radInvestigation2Result", "");
                                form.setValue("radInvestigation2Comment", "");
                              }}
                            >
                              -
                            </button>
                          }
                        />
                      )}
                    </div>
                    <div>
                      <FormField name="otherRadInvestigations">
                        <div>
                          <label
                            className={style.addReferralFormLabel}
                            style={{ fontWeight: "400", fontSize: "13px" }}
                          >
                            Other Rad Investigation:
                          </label>
                          <input
                            className={style.winXpInputWrapper}
                            style={{ width: "100%" }}
                            {...form.register("otherRadInvestigations")}
                          />
                        </div>
                      </FormField>
                    </div>
                  </div>
                </div>

                <FormField name="informationForContrastCt">
                  <div className={style.formItemWrapper}>
                    <label className={style.addReferralFormLabel}>
                      Information for Contrast CT:
                    </label>
                    <div></div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label className={style.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.watch("historyOfAsthma")}
                          onChange={(e) => {
                            form.setValue("historyOfAsthma", e.target.checked);
                          }}
                        />
                        History of asthma
                      </label>
                      <label className={style.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.watch("historyOfRentalInsufficiency")}
                          onChange={(e) => {
                            form.setValue(
                              "historyOfRentalInsufficiency",
                              e.target.checked
                            );
                          }}
                        />
                        History of Rental Insufficiency
                      </label>
                      <label className={style.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.watch("historyOfAllergyToContrastDye")}
                          onChange={(e) => {
                            form.setValue(
                              "historyOfAllergyToContrastDye",
                              e.target.checked
                            );
                          }}
                        />
                        History of Allergy to Contrast Dye
                      </label>
                    </div>
                  </div>
                </FormField>
              </div>
              <FormField name="hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer">
                <div className={style.formItemWrapperLast}>
                  <div></div>
                  <label className={style.addReferralFormLabel}>
                    <div className={style.info}>
                      Has patient been advised of possible diagnosis of lung
                      cancer?
                    </div>
                  </label>

                  <RadioGroup
                    value={
                      form.watch(
                        "hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer"
                      ) || ""
                    }
                    onChange={(e) => {
                      form.setValue(
                        "hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer",
                        e.target.value
                      );
                    }}
                  >
                    <div className={style.radioGroup}>
                      <FormControlLabel
                        value="Yes"
                        control={<Radio size="xs" sx={radioSx} />}
                        label={<span style={{ fontSize: "13px" }}>Yes</span>}
                      />
                      <FormControlLabel
                        value="No"
                        control={<Radio size="xs" sx={radioSx} />}
                        label={<span style={{ fontSize: "13px" }}>No</span>}
                      />
                    </div>
                  </RadioGroup>
                </div>
              </FormField>
            </form>
          </FormProvider>
        </div>
        <FormProvider {...form}>
          <CurrentMedicationSection form={form} />
        </FormProvider>
      </ItemFormWrapper>
    </>
  );
};
